import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import range from 'lodash/range';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';

import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import units from '../../../../../lib/units';
import numericInput from '../../../../../lib/numericInput';

const percentOptions = range(0.05, 1.05, 0.05).map(value => ({
  value,
  display: `${Math.round(value * 100)}%`,
}));

const carbonDensity = (isMetric, t) => ({
  massUnit: isMetric ? t('metric tons', { ns: 'units' }) : t('short tons', { ns: 'units' }),
  value: isMetric ?
    numericInput.format(0.58 / units.SHORT_TON_PER_METRIC_TON, { decimalPlaces: 2 }) :
    numericInput.format(0.58, { decimalPlaces: 2 }),
});

const wasteDensity = (isMetric, t) => {
  let value = 225;
  if (isMetric) {
    value = units.lbsToKg(value) * Math.pow(units.FT_PER_METER / 3, 3);
  }
  return {
    value: numericInput.format(value),
    massUnit: isMetric ? t('kilograms', { ns: 'units' }) : t('pounds', { ns: 'units' }),
    volumeUnit: `${isMetric ? t('m', { ns: 'units' }) : t('yd', { ns: 'units' })}<sup>3</sup>`,
  };
};

const ReduceYourWaste = ({
  t,
  isMetric,
  locale,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('My household currently throws out')}
        <InlineInput
          apiKey={'input_takeaction_reduce_comm_waste_per_week'}
        />
        <span
          dangerouslySetInnerHTML={{
            __html: t('{{units}} of trash each week. I will reduce my trash by', {
              units: `${isMetric ? t('m', { ns: 'units' }) : t('yd', { ns: 'units' })}<sup>3</sup>`,
            }),
          }}
        />
        <InlineSelect
          options={percentOptions}
          apiKey={'input_takeaction_reduce_comm_waste_percent'}
        />.
      </p>
    </div>
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          <span
            dangerouslySetInnerHTML={{
              __html: t('{{value}} {{massUnit}} of waste per {{volumeUnit}}', wasteDensity(isMetric, t)),
            }}
          />
          &nbsp;(<a
            href="https://www.epa.gov/warm"
            target="_blank"
            rel="noopener noreferrer"
          >&nbsp;{t('EPA Warm Model')}</a>&nbsp;)
          {locale === 'fr' && ' (en anglais seulement).'}
          {locale !== 'fr' && '.'}
        </li>
        <li>
          <span
            dangerouslySetInnerHTML={{
              __html: t('{{value}} tCO<sub>2</sub> (from methane) per {{massUnit}} of waste', carbonDensity(isMetric, t)),
            }}
          />
          &nbsp;(<a
            href="https://www.epa.gov/warm"
            target="_blank"
            rel="noopener noreferrer"
          >&nbsp;{t('EPA Warm Model')}</a>&nbsp;)
          {locale === 'fr' && ' (en anglais seulement).'}
          {locale !== 'fr' && '.'}
        </li>
      </ul>
    </div>
  </div>
);

ReduceYourWaste.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => []
.reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  locale: s.locale(state),
  isMetric: s.isMetric(state),
});

const mapDispatchToProps = (_dispatch, _ownProps) => ({});

export default wrap(
  withT('reduceCommWaste'),
  connect(mapStateToProps, mapDispatchToProps)(ReduceYourWaste),
);
